import { useCallback, useEffect, useState } from 'react';

import {
  CRYPTO_NETWORK_LABELS,
  PAYOUT_METHOD_LABELS,
  formatCents,
  type AdminPayout,
  type AdminPayoutsResponse,
  type PayoutInstructionsResponse,
  type PayoutMethodKind,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CellNote,
  ConfirmButton,
  DataTable,
  EmptyState,
  Fact,
  Facts,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  TextField,
  When,
  statusLabel,
  statusTone,
  type Column,
} from '../../ui';

/**
 * The payouts queue: the operator's Monday.
 *
 * ## The order of the screen is the order of the work
 *
 * Batches first — that is the one button that matters on a Monday morning —
 * then the payouts themselves, grouped by what they need: approval, payment,
 * or nothing. A payout in flight is the only one with a form, because it is
 * the only one where the operator has just done something in another tab and
 * needs to record it here.
 *
 * ## The destination is not on this page
 *
 * Every row carries the MASK. The real address is one click, one payout at a
 * time, through `/admin/payouts/:id/instructions`, and that read is itself
 * audited. A queue that listed everybody's PayPal addresses would be a list
 * worth stealing, screenshotting or leaving open on a second monitor.
 */
export function AdminPayouts(): React.JSX.Element {
  const [data, setData] = useState<AdminPayoutsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [instructions, setInstructions] = useState<PayoutInstructionsResponse | null>(null);
  const [references, setReferences] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setData(await apiGet<AdminPayoutsResponse>('/admin/payouts'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the payouts.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<string>): Promise<void> {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await fn());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const cutBatch = (): Promise<void> =>
    act(async () => {
      const body = await apiSend<{
        batch: { batchKey: string; payouts: number; amountCents: number };
      }>('POST', '/admin/payouts/batch/run', {});
      return `Batch ${body.batch.batchKey}: ${body.batch.payouts} payout(s), ${formatCents(body.batch.amountCents)} waiting for approval.`;
    });

  const approve = (batchKey: string): Promise<void> =>
    act(async () => {
      const body = await apiSend<{
        batch: {
          approved: number;
          dispatched: number;
          amountCents: number;
          refused: Array<{ message: string }>;
        };
      }>('POST', `/admin/payouts/batch/${encodeURIComponent(batchKey)}/approve`, { confirm: true });
      const refused = body.batch.refused.length;
      return `${body.batch.approved} approved, ${body.batch.dispatched} instruction(s) issued for ${formatCents(body.batch.amountCents)}${refused > 0 ? `; ${refused} refused by the rail` : ''}.`;
    });

  const showInstructions = (payoutId: string): Promise<void> =>
    act(async () => {
      const body = await apiGet<PayoutInstructionsResponse>(
        `/admin/payouts/${payoutId}/instructions`,
      );
      setInstructions(body);
      return body.error ? `Could not read the destination: ${body.error}` : 'Instructions below.';
    });

  const markPaid = (payout: AdminPayout): Promise<void> =>
    act(async () => {
      const reference = (references[payout.id] ?? '').trim();
      if (!reference) throw new ApiError(400, 'reference_required', 'Enter the payment reference.');
      await apiSend('POST', `/admin/payouts/${payout.id}/paid`, { reference });
      setInstructions(null);
      return `Paid ${formatCents(payout.amountCents)} to ${payout.creatorName}, reference ${reference}.`;
    });

  const markFailed = (payout: AdminPayout): Promise<void> =>
    act(async () => {
      const reason = (references[`${payout.id}:reason`] ?? '').trim();
      if (!reason) throw new ApiError(400, 'reason_required', 'Say why it failed.');
      await apiSend('POST', `/admin/payouts/${payout.id}/failed`, { reason });
      return 'Marked failed. The earnings are available again and join the next batch.';
    });

  if (data === null) {
    if (error) {
      return (
        <>
          <PageHeader eyebrow="Operator" title="Payouts" />
          <Alert tone="critical" title="Could not load the payouts.">
            <span>{error} No money has moved.</span>
          </Alert>
        </>
      );
    }
    return <LoadingPage eyebrow="Operator" title="Payouts" stats />;
  }

  const waiting = data.payouts.filter((p) => p.requested);
  const inFlight = data.payouts.filter(
    (p) => p.status === 'APPROVED' || p.status === 'PROCESSING',
  );
  const settled = data.payouts.filter((p) => p.status === 'PAID' || p.status === 'FAILED');
  const waitingTotal = waiting.reduce((sum, p) => sum + p.amountCents, 0);
  const inFlightTotal = inFlight.reduce((sum, p) => sum + p.amountCents, 0);

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Payouts"
        lead={`The batch is cut automatically every Monday at 09:00 ${data.timeZone}. The minimum payout is ${formatCents(data.minimumCents)}.`}
        actions={
          /* Ghost, not primary. The one primary on this page is "Approve this
             batch" - the button that moves money. Cutting a batch is a
             rehearsal. */
          <Button onClick={() => void cutBatch()} disabled={busy}>
            Cut the batch now
          </Button>
        }
      />

      {message && <Alert tone="positive">{message}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <StatGrid>
        <Stat
          label="Waiting for approval"
          value={<Money cents={waitingTotal} />}
          note={`${waiting.length} payout${waiting.length === 1 ? '' : 's'}`}
          emphasis={waiting.length > 0}
        />
        <Stat
          label="In flight"
          value={<Money cents={inFlightTotal} />}
          note={`${inFlight.length} to pay and record`}
        />
        <Stat label="Settled" value={<Num value={settled.length} />} />
        <Stat label="Minimum" value={<Money cents={data.minimumCents} />} />
      </StatGrid>

      <Card title="Batches" sub="A batch is approved as a whole. Nothing leaves before you approve it.">
        <BatchTable data={data} busy={busy} onApprove={approve} />
      </Card>

      <Card
        title={`Waiting for approval (${waiting.length})`}
        sub="Creators have asked for these. They join the next batch."
      >
        <PayoutTable payouts={waiting} emptyTitle="Nobody has asked for a payout." />
      </Card>

      <Card
        title={`In flight (${inFlight.length})`}
        sub="Approved and issued. Pay each one, then record what you paid it with."
      >
        {inFlight.length === 0 ? (
          <EmptyState title="Nothing has been issued.">
            Approving a batch issues its instructions and moves its payouts here.
          </EmptyState>
        ) : (
          <ul className="rows">
            {inFlight.map((payout) => (
              <li key={payout.id}>
                <div className="row-head">
                  <span className="row-title">
                    <Money cents={payout.amountCents} /> to {payout.creatorName}
                  </span>
                  <Badge tone={statusTone(payout.status, 'payout')}>
                    {statusLabel(payout.status, 'payout')}
                  </Badge>
                </div>
                <Facts>
                  <Fact label="Method">
                    {payout.methodKind
                      ? PAYOUT_METHOD_LABELS[payout.methodKind as PayoutMethodKind]
                      : 'No method on file'}
                  </Fact>
                  {payout.cryptoNetwork && (
                    <Fact label="Network">{CRYPTO_NETWORK_LABELS[payout.cryptoNetwork]}</Fact>
                  )}
                  {/* The mask, always. */}
                  <Fact label="Sent to">{payout.methodLabel ?? '—'}</Fact>
                  <Fact label="Earnings">
                    <Num value={payout.earnings} />
                  </Fact>
                </Facts>

                <div className="actions">
                  <Button onClick={() => void showInstructions(payout.id)} disabled={busy}>
                    Show me what to pay
                  </Button>
                </div>

                {instructions && instructions.payoutId === payout.id && (
                  <Alert tone="info" title={instructions.summary}>
                    {instructions.destination ? (
                      <span>
                        Send it to <code className="chip break">{instructions.destination}</code>
                      </span>
                    ) : (
                      <span className="tone-critical">{instructions.error}</span>
                    )}
                    {/* USDT: the chain in its own line, because it is the
                        thing that cannot be got wrong, then the fee note from
                        USDT_FEE_POLICY. The reference to record afterwards is
                        the transaction hash, and the Worker refuses anything
                        that is not one. */}
                    {instructions.crypto && (
                      <p data-testid="instructions-crypto">
                        <strong>
                          {instructions.crypto.asset} on {instructions.crypto.networkLabel}.
                        </strong>{' '}
                        {instructions.crypto.feeSentence} Record the transaction hash as the
                        reference.
                        {instructions.crypto.explorerUrl && (
                          <>
                            {' '}
                            <a href={instructions.crypto.explorerUrl} target="_blank" rel="noreferrer noopener">
                              Check the address on the explorer
                            </a>
                            .
                          </>
                        )}
                      </p>
                    )}
                    {/* Where it leaves FROM, from Settings → Payouts: the PayPal
                        account, the sending wallet's tail, or the wire notes. */}
                    {instructions.paidFrom && (instructions.paidFrom.paypalEmail || instructions.paidFrom.usdtWalletLast6 || instructions.paidFrom.wireNotes) && (
                      <p data-testid="instructions-paid-from">
                        {instructions.paidFrom.paypalEmail && (
                          <>
                            Send it from <code className="chip">{instructions.paidFrom.paypalEmail}</code>.{' '}
                          </>
                        )}
                        {instructions.paidFrom.usdtWalletLast6 && (
                          <>
                            Send it from the wallet ending <code className="chip">…{instructions.paidFrom.usdtWalletLast6}</code>.{' '}
                          </>
                        )}
                        {instructions.paidFrom.wireNotes && (
                          <>
                            <strong>Wire notes:</strong>{' '}
                            <span className="break">{instructions.paidFrom.wireNotes}</span>
                          </>
                        )}
                      </p>
                    )}
                    {/* A referral bonus is an ordinary Earning and the batch pays
                        it without knowing it is one, which is exactly why the
                        amount alone cannot say so. The person typing it into
                        PayPal is the one who gets asked about it. */}
                    {instructions.referralBonusCents > 0 && (
                      <p className="muted" data-testid="instructions-referral">
                        Includes {formatCents(instructions.referralBonusCents)} of referral bonus
                        over {instructions.referralBonusCount}{' '}
                        {instructions.referralBonusCount === 1 ? 'bonus' : 'bonuses'} — money for a
                        friend&rsquo;s clips, not this creator&rsquo;s own.
                      </p>
                    )}
                  </Alert>
                )}

                <div className="field-row">
                  <TextField
                    label="Payment reference"
                    type="text"
                    value={references[payout.id] ?? ''}
                    onChange={(e) => setReferences((r) => ({ ...r, [payout.id]: e.target.value }))}
                    placeholder={payout.cryptoNetwork ? 'The transaction hash' : 'PAYPAL-4412'}
                    hint={
                      payout.cryptoNetwork
                        ? 'The on-chain transaction hash. Anything else is refused.'
                        : 'What you can find this payment by, later.'
                    }
                  />
                  <TextField
                    label="Or why it failed"
                    type="text"
                    value={references[`${payout.id}:reason`] ?? ''}
                    onChange={(e) =>
                      setReferences((r) => ({ ...r, [`${payout.id}:reason`]: e.target.value }))
                    }
                    placeholder="PayPal refused the address"
                    hint="The creator is shown this. Marking it failed returns the earnings to the next batch."
                  />
                </div>

                <div className="actions">
                  <Button variant="primary" onClick={() => void markPaid(payout)} disabled={busy}>
                    Mark paid
                  </Button>
                  <ConfirmButton
                    label="Mark failed"
                    title={`Mark this payout failed?`}
                    question="The earnings become available again and join the next batch. The creator is shown your reason."
                    confirmLabel="Mark it failed"
                    disabled={busy}
                    onConfirm={() => void markFailed(payout)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={`Settled (${settled.length})`} sub="Paid, or failed with a reason.">
        <PayoutTable payouts={settled} emptyTitle="Nothing has settled yet." />
      </Card>
    </>
  );
}

function BatchTable({
  data,
  busy,
  onApprove,
}: {
  data: AdminPayoutsResponse;
  busy: boolean;
  onApprove: (batchKey: string) => Promise<void>;
}): React.JSX.Element {
  type Batch = AdminPayoutsResponse['batches'][number];
  const columns: Array<Column<Batch>> = [
    { key: 'batch', header: 'Batch', primary: true, cell: (b) => b.batchKey },
    {
      key: 'payouts',
      header: 'Payouts',
      numeric: true,
      align: 'end',
      cell: (b) => <Num value={b.payouts} />,
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      align: 'end',
      cell: (b) => <Money cents={b.amountCents} />,
    },
    {
      key: 'states',
      header: 'Where it is',
      cell: (b) => (
        <>
          {b.statuses.map((status) => (
            <Badge key={status} tone={statusTone(status, 'payout')}>
              {statusLabel(status, 'payout')}
            </Badge>
          ))}
        </>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      align: 'end',
      cell: (b) =>
        b.statuses.includes('DRAFT') ? (
          <ConfirmButton
            label="Approve this batch"
            variant="primary"
            small
            title={`Approve ${b.batchKey}?`}
            question={`This approves ${b.payouts} payout(s) for ${formatCents(b.amountCents)} and issues their instructions. Approval is the last gate before money leaves.`}
            confirmLabel="Approve it"
            disabled={busy}
            onConfirm={() => void onApprove(b.batchKey)}
          />
        ) : (
          <span className="muted">—</span>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.batches}
      getKey={(b) => b.batchKey}
      empty={
        <EmptyState title="No batch has been cut yet.">
          A batch is cut every Monday, or now with the button above.
        </EmptyState>
      }
    />
  );
}

function PayoutTable({
  payouts,
  emptyTitle,
}: {
  payouts: AdminPayout[];
  emptyTitle: string;
}): React.JSX.Element {
  const columns: Array<Column<AdminPayout>> = [
    {
      key: 'creator',
      header: 'Creator',
      primary: true,
      cell: (payout) => (
        <>
          {payout.creatorName}
          <CellNote>{payout.creatorEmail}</CellNote>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (payout) => <Money cents={payout.amountCents} />,
    },
    {
      key: 'earnings',
      header: 'Earnings',
      numeric: true,
      align: 'end',
      cell: (payout) => <Num value={payout.earnings} />,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (payout) => (
        <>
          <Badge tone={statusTone(payout.requested ? 'REQUESTED' : payout.status, 'payout')}>
            {payout.requested ? 'Requested' : statusLabel(payout.status, 'payout')}
          </Badge>
          {payout.reason && <CellNote>{payout.reason}</CellNote>}
        </>
      ),
    },
    /* The mask, always — and the chain beside it for a USDT row. */
    {
      key: 'sentTo',
      header: 'Sent to',
      cell: (payout) => (
        <>
          {payout.methodLabel ?? <span className="muted">—</span>}
          {payout.cryptoNetwork && <CellNote>USDT on {CRYPTO_NETWORK_LABELS[payout.cryptoNetwork]}</CellNote>}
        </>
      ),
    },
    {
      key: 'when',
      header: 'Paid',
      cell: (payout) => <When value={payout.paidAt} />,
    },
    {
      key: 'reference',
      header: 'Reference',
      cell: (payout) =>
        payout.paymentReference ? (
          <code className="chip quiet">{payout.paymentReference}</code>
        ) : (
          <span className="muted">—</span>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={payouts}
      getKey={(payout) => payout.id}
      empty={<EmptyState title={emptyTitle}>Nothing needs you here right now.</EmptyState>}
    />
  );
}
